import React from "react";
import { unstable_batchedUpdates as batch, flushSync } from "react-dom";
import styled from "styled-components";
import { PropError, PropLabel, PropNote, PropOption, PropSelected } from "../../PropX";
import { useAutocompleteFocusCapture } from "../../hooks/useAutocompleteFocusCapture";
import { useDebounce } from "../../hooks/useDebounce";
import { useEvent } from "../../hooks/useEvent";
import { useHTMLInputElementValidationProjection } from "../../hooks/useHTMLInputElementValidationProjection";
import { style } from "../../style";
import { LooseAutocompleteInput } from "../LooseAutocompleteInput";
import { SelectedTokensBucket } from "./SelectedTokensBucket";
import { splitCsvTokens } from "./splitCsvTokens";

const PrefixWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`;

const ClearAllButton = styled.a.attrs({ role: "button" })`
  margin-bottom: ${style.vars.space["2"]};
  font-size: ${style.vars.fontSize.small};
  min-width: 48px; /* this is the width of the text */
  text-align: right;
`;

type Props = {
  /**
   * The name of the form field.
   */
  name: string;
  /**
   * The current search term.
   */
  search: string;
  /**
   * The options to display in the dropdown menu.
   */
  options: PropOption[];
  /**
   * The options selected from the dropdown menu. If any `Selected.value` matches an option, they will be removed from the list.
   */
  selected: PropSelected[];

  /**
   * A callback to update the search term. The term must pass the validation function before this is called.
   */
  onChangeSearch(search: string): void;

  /**
   * When a token is selected, this callback is called will _all_ of the selected tokens.
   */
  onSelectMany(value: PropSelected[]): void;

  /**
   * The placeholder text for the input element.
   * @default "Enter tag name"
   */
  placeholder?: string;

  /**
   * When the input is disabled, it will not accept input.
   * @default false
   */
  disabled?: boolean;

  /**
   * When the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * When a token is created from text input, this sets the icon to use when displaying the token if not present from the list.
   * To omit the icon, pass `null`.
   * @default "product-tag"
   */
  defaultSearchInputIcon?: string | null;

  /**
   * Whether or not to filter the options by the current value of `search`.
   * @default true
   */
  filterOptions?: boolean;

  /**
   * A message to display after the input.
   * @default null
   */
  note?: React.ReactNode;

  /**
   * A label to display above the input.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * A node to display on the right side of the text input.
   * @default undefined
   */
  leftSide?: React.ReactNode;

  /**
   * A node to display on the right side of the text input.
   * @default undefined
   */
  rightSide?: React.ReactNode;

  /**
   * An optional header to display above the list of options.
   * @default null
   */
  listHeader?: React.ReactNode;

  /**
   * An optional footer to display below the list of options.
   * @default null
   */
  listFooter?: React.ReactNode;

  /**
   * The error message to display on the search input.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "change"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * A regular expression to validate string input against.
   * @default undefined
   */
  pattern?: string;

  /**
   * Whether the input should clear the search term when an option is selected.
   * @default true
   */
  shouldClearSearchOnSelect?: boolean;

  /**
   * The delay in milliseconds to wait before filtering the options. Only use if you are having performance issues.
   * @default 200
   */
  UNSAFE_filterDelay?: number;

  /**
   * The number of items to render above and below the visible items in the list.
   * @private
   * @default 10
   */
  UNSAFE_overscanCount?: number;

  /**
   * Whether to round the shape of the selected tokens.
   */
  UNSAFE_roundedTokens?: boolean;

  /**
   * The type of the input.
   * @default "search"
   */
  UNSAFE_type?: "number" | "text" | "password" | "email" | "tel" | "url" | "search";

  /**
   * Testing utility tag.
   * @default "dynamic-input"
   */
  "data-testid"?: string;
};

export const LooseDynamicInput: React.FC<Props> = (props) => {
  const {
    name,
    search,
    onChangeSearch,
    options,
    selected,
    listHeader = null,
    listFooter = null,
    placeholder = "",
    disabled = false,
    required = false,
    filterOptions = true,
    defaultSearchInputIcon = "product-tag",
    label,
    leftSide,
    rightSide,
    pattern,
    validateOn = "change",
    UNSAFE_filterDelay = 200,
    UNSAFE_overscanCount = 10,
    UNSAFE_roundedTokens = false,
    shouldClearSearchOnSelect = true,
    UNSAFE_type = "search",
  } = props;
  const note = PropNote.normalize(props.note);

  const testId = props["data-testid"] ?? "dynamic-input";

  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * This ref is used to get the correct blurring behavior to work. That is,
   *
   * If the user typed something in between selecting any dropdown value then it should create a tag from it on blur.
   * If the user typed in order to find a specific tag from the dropdown, then we should NOT create a tag from it on blur.
   *
   * Essentially, we assume the if the user typed and selected a value, what is currently in the input was a search term.
   */
  const searchChangedSinceLastSelectedRef = React.useRef(false);

  const onChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((ev) => {
    const next = ev.currentTarget.value;

    searchChangedSinceLastSelectedRef.current = true;

    onChangeSearch(next);
  });

  const [errors, propsForInput, force, inputFocused] = useHTMLInputElementValidationProjection(
    { ...props, onChange },
    { ref: inputRef, validateOn }
  );

  /**
   * When filtering search terms, we want to debounce the input to avoid excessive re-renders.
   */
  const filterSearch = useDebounce(filterOptions ? search : "", filterOptions == false || search === "" ? 0 : UNSAFE_filterDelay);

  const bin = React.useMemo(
    () => PropOption.bin(options, selected, { filterSearch, filterSelectedFromItems: true }),
    [options, selected, filterSearch]
  );

  const onSelectMany = useEvent((selected: PropSelected[]) => {
    const cache: { [value: string]: boolean } = {};
    const next: PropSelected[] = [];

    for (const v of selected) {
      if (!cache[v.value]) {
        cache[v.value] = true;
        next.push(v);
      }
    }

    props.onSelectMany(next);
  });

  const findOption = useEvent((key: string) => {
    return PropOption.fullMatchBySearch(options, key) ?? { key, value: key, icon: defaultSearchInputIcon ?? undefined };
  });

  const onSelect = useEvent((key: string) => {
    const next = findOption(key);

    batch(() => {
      if (shouldClearSearchOnSelect) {
        onChangeSearch("");
      }

      onSelectMany([...selected, next]);
    });
  });

  const handleRemoveToken = useEvent((value: string) => {
    const next = selected.filter((o) => o.value !== value);
    onSelectMany(next);

    if (next.length === 0) {
      inputRef.current?.focus();
    }
  });

  const onSubmitSearch = useEvent((search: string): void => {
    const skip = search.length === 0 || selected.some((o) => o.value === search);

    /**
     * Set this to false because the input is completed. Regardless of the outcome,
     * the user is no longer "searching" for a token and has attempted to enter whatever
     * is in the search box.
     */
    searchChangedSinceLastSelectedRef.current = false;

    if (skip) {
      onChangeSearch("");
      return;
    }

    if (errors.input) {
      return;
    }

    const token = findOption(search);

    batch(() => {
      onChangeSearch("");
      onSelectMany([...selected, token]);
    });
  });

  /**
   * A keyboard handler to create a new token.
   */
  const onKeyDown = useEvent<React.KeyboardEventHandler<HTMLInputElement>>((ev) => {
    const input = inputRef.current;

    if (!input) return;

    switch (ev.key) {
      case "Escape": {
        ev.preventDefault();

        flushSync(() => onSubmitSearch(""));
        force.checkValidity();

        return;
      }

      case "Enter":
      case " ":
      case ",": {
        ev.preventDefault();

        const valid = force.checkValidity();

        if (valid) {
          /**
           * This prevents the keypress from triggering the `handleOnChange` callback which
           * would normally be triggered _after_ this callback.
           */
          onSubmitSearch(search);
          return;
        }
      }
    }
  });

  const capture = useAutocompleteFocusCapture();

  const onBlurCapture = useEvent<React.FocusEventHandler<HTMLDivElement>>((ev) => {
    capture.onBlurCapture(ev);

    if (searchChangedSinceLastSelectedRef.current === false) {
      return;
    }

    if (search === "") {
      return;
    }

    const currentTarget = ev.currentTarget;

    /**
     * We have to wait until the next tick before deciding what to do here because
     * ariakit will BRIEFLY make the body the document.activeElement when clicking input the
     * select menu. Waiting until the next tick ensures that clicking to tabbing to the dropdown
     * will not close the dropdown. Otherwise we will have the following bug:
     *   1. User types "potato".
     *   2. New options appear matching the term: "potato1", "potato2", etc.
     *   3. User clicks "potato1".
     *   4. Input is blurred.
     *   5. document.activeElement is the body.
     *   6. "potato" is added as a token.
     */

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        onSubmitSearch(search);
      }
    });
  });

  const onPaste = useEvent<React.ClipboardEventHandler<HTMLInputElement>>((ev) => {
    ev.preventDefault();

    const csv = ev.clipboardData.getData("text");
    const result = splitCsvTokens(csv, errors, pattern, UNSAFE_type);
    const tags = result.tokens.map(findOption);

    batch(() => {
      onSelectMany([...selected, ...tags]);

      if (result.success) {
        force.checkValidity();
      } else {
        force.displayErrorMessage(result.error.message);
      }
    });
  });

  const onClearAll = useEvent<React.MouseEventHandler<HTMLAnchorElement>>((ev) => {
    ev.preventDefault();
    onSelectMany([]);
  });

  const prefix = (() => {
    if (!label) {
      return null;
    }

    return (
      <PrefixWrapper>
        {label && <div>{PropLabel.renderForBeforeInput(label, { htmlFor: name, required, invalid: propsForInput.invalid })}</div>}
        {selected.length > 0 && (
          <ClearAllButton data-testid="clear-all" onClick={onClearAll}>
            clear all
          </ClearAllButton>
        )}
      </PrefixWrapper>
    );
  })();

  const suffix = (() => {
    const showNote = note.persist || (inputFocused && capture.focused);

    return (
      <>
        {showNote && PropNote.renderForAfterInput(note.value)}
        {PropError.renderGroupAfterInput(errors)}
      </>
    );
  })();

  return (
    <div onFocusCapture={capture.onFocusCapture} onBlurCapture={onBlurCapture}>
      {prefix}
      {bin.selected.length > 0 && (
        <SelectedTokensBucket
          selected={bin.selected}
          disabled={disabled}
          onRemove={handleRemoveToken}
          roundedTokens={UNSAFE_roundedTokens}
        />
      )}
      <LooseAutocompleteInput
        id={name}
        // Doing this to be explicit about not defining a name. With a name, a form will ignore the value of the input on submit.
        name={undefined}
        ref={inputRef}
        placeholder={placeholder}
        value={search}
        onSelect={onSelect}
        items={bin.items}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        disabled={disabled}
        leftSide={leftSide}
        rightSide={rightSide}
        pattern={pattern}
        listHeader={listHeader}
        listFooter={listFooter}
        data-testid={testId}
        UNSAFE_borderRadiusTop={selected.length > 0 ? "none" : "base"}
        UNSAFE_popoverRepositionKey={selected.length}
        UNSAFE_forceHidePopover={!capture.focused}
        UNSAFE_overscanCount={UNSAFE_overscanCount}
        UNSAFE_type={UNSAFE_type}
        {...propsForInput}
      />
      {suffix}
      <input
        type="hidden"
        name={name}
        value={bin.selected
          .filter((option) => option.state === "valid")
          .map((option) => option.value)
          .join(",")}
      />
    </div>
  );
};

LooseDynamicInput.displayName = "LooseDynamicInput";
